import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
  Divider,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDeploy } from '../contexts/DeployContext';
import { useGlobal } from '../contexts/GlobalContext';
import { EditorState } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';
import { defaultKeymap } from '@codemirror/commands';
import { javascript } from '@codemirror/lang-javascript';
import { generateDsl, parseDsl } from './configDsl';

const CACHE_AGE_DEFAULTS = {
  main_max_cache_age_s: 7200,
  primary_max_cache_age_s: 7200,
  secondary_max_cache_age_s: 600,
};

const BUILDING_TIMEOUT_MS_DEFAULT = 250;
const PAGE_SIZE_DEFAULT = 25;

function ConfigForm({ config, onSaved, onClear }) {
  const { accountId, userId } = useGlobal();
  const { deployApi } = useDeploy();

  const cfg = config?.feed_config ?? config?.deployment_config ?? {};

  const [name, setName] = useState(config?.name ?? '');
  const [description, setDescription] = useState(config?.description ?? '');
  const [pageSize, setPageSize] = useState(cfg.page_size ?? PAGE_SIZE_DEFAULT);
  const [mainAlgos, setMainAlgos] = useState(() => cfg.main_algos ?? []);
  const [mainMaxCacheAgeSec, setMainMaxCacheAgeSec] = useState(cfg.main_max_cache_age_s ?? CACHE_AGE_DEFAULTS.main_max_cache_age_s);
  const [buildingTimeoutMs, setBuildingTimeoutMs] = useState(cfg.building_timeout_ms ?? BUILDING_TIMEOUT_MS_DEFAULT);
  const [primaryFallback, setPrimaryFallback] = useState(cfg.primary_fallback ?? '');
  const [primaryMaxCacheAgeSec, setPrimaryMaxCacheAgeSec] = useState(cfg.primary_max_cache_age_s ?? CACHE_AGE_DEFAULTS.primary_max_cache_age_s);
  const [secondaryFallback, setSecondaryFallback] = useState(cfg.secondary_fallback ?? '');
  const [secondaryMaxCacheAgeSec, setSecondaryMaxCacheAgeSec] = useState(cfg.secondary_max_cache_age_s ?? CACHE_AGE_DEFAULTS.secondary_max_cache_age_s);
  const [exclusionMaxItems, setExclusionMaxItems] = useState(() => {
    const r = (cfg.exclusion_rules ?? []).find((x) => x.rule === 'max_items_with_same_field');
    return r ? { enabled: true, field: r.field ?? '', value: r.value ?? 1 } : { enabled: false, field: '', value: 1 };
  });
  const [exclusionMinDistance, setExclusionMinDistance] = useState(() => {
    const r = (cfg.exclusion_rules ?? []).find((x) => x.rule === 'min_distance_between_items_with_same_field');
    return r ? { enabled: true, field: r.field ?? '', distance: r.distance ?? 1 } : { enabled: false, field: '', distance: 1 };
  });
  const [exclusionHide1, setExclusionHide1] = useState(() => {
    const hideRules = (cfg.exclusion_rules ?? []).filter((x) => x.rule === 'hide_items_with_same_field');
    const first = hideRules[0];
    return first ? { enabled: true, field: first.field ?? '' } : { enabled: false, field: '' };
  });
  const [exclusionHide2, setExclusionHide2] = useState(() => {
    const hideRules = (cfg.exclusion_rules ?? []).filter((x) => x.rule === 'hide_items_with_same_field');
    const second = hideRules[1];
    return second ? { enabled: true, field: second.field ?? '' } : { enabled: false, field: '' };
  });
  const [exclusionItemsShownNTimes, setExclusionItemsShownNTimes] = useState(() => {
    const r = (cfg.exclusion_rules ?? []).find((x) => x.rule === 'exclude_items_shown_n_times');
    return r ? { enabled: true, value: r.value ?? 1 } : { enabled: false, value: 1 };
  });
  const [exclusionItemsShownLastNSeconds, setExclusionItemsShownLastNSeconds] = useState(() => {
    const r = (cfg.exclusion_rules ?? []).find((x) => x.rule === 'exclude_items_shown_last_n_seconds');
    return r ? { enabled: true, value: r.value ?? 3600 } : { enabled: false, value: 3600 };
  });
  const [isPublic, setIsPublic] = useState(config?.is_public ?? false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // DSL toggle state (mirror of NotificationForm's Form/DSL tabs)
  const [mode, setMode] = useState('form');
  const [dslText, setDslText] = useState('');
  const [dslError, setDslError] = useState(null);
  const editorContainerRef = useRef(null);
  const editorViewRef = useRef(null);

  const [feeds, setFeeds] = useState([]);
  const [feedsLoading, setFeedsLoading] = useState(false);

  const feedsWithNoneInputs = feeds.filter((f) => f.feed_inputs === 'none');
  const feedsMinifeed = feeds.filter((f) => f.feed_type === 'minifeed');

  useEffect(() => {
    const c = config?.feed_config ?? config?.deployment_config ?? {};
    setName(config?.name ?? '');
    setDescription(config?.description ?? '');
    setPageSize(c.page_size ?? PAGE_SIZE_DEFAULT);
    setMainAlgos(c.main_algos ?? []);
    setMainMaxCacheAgeSec(c.main_max_cache_age_s ?? CACHE_AGE_DEFAULTS.main_max_cache_age_s);
    setBuildingTimeoutMs(c.building_timeout_ms ?? BUILDING_TIMEOUT_MS_DEFAULT);
    setPrimaryFallback(c.primary_fallback ?? '');
    setPrimaryMaxCacheAgeSec(c.primary_max_cache_age_s ?? CACHE_AGE_DEFAULTS.primary_max_cache_age_s);
    setSecondaryFallback(c.secondary_fallback ?? '');
    setSecondaryMaxCacheAgeSec(c.secondary_max_cache_age_s ?? CACHE_AGE_DEFAULTS.secondary_max_cache_age_s);
    const maxR = (c.exclusion_rules ?? []).find((x) => x.rule === 'max_items_with_same_field');
    setExclusionMaxItems(maxR ? { enabled: true, field: maxR.field ?? '', value: maxR.value ?? 1 } : { enabled: false, field: '', value: 1 });
    const minR = (c.exclusion_rules ?? []).find((x) => x.rule === 'min_distance_between_items_with_same_field');
    setExclusionMinDistance(minR ? { enabled: true, field: minR.field ?? '', distance: minR.distance ?? 1 } : { enabled: false, field: '', distance: 1 });
    const hideRules = (c.exclusion_rules ?? []).filter((x) => x.rule === 'hide_items_with_same_field');
    setExclusionHide1(hideRules[0] ? { enabled: true, field: hideRules[0].field ?? '' } : { enabled: false, field: '' });
    setExclusionHide2(hideRules[1] ? { enabled: true, field: hideRules[1].field ?? '' } : { enabled: false, field: '' });
    const shownNTimesR = (c.exclusion_rules ?? []).find((x) => x.rule === 'exclude_items_shown_n_times');
    setExclusionItemsShownNTimes(shownNTimesR ? { enabled: true, value: shownNTimesR.value ?? 1 } : { enabled: false, value: 1 });
    const shownLastNR = (c.exclusion_rules ?? []).find((x) => x.rule === 'exclude_items_shown_last_n_seconds');
    setExclusionItemsShownLastNSeconds(shownLastNR ? { enabled: true, value: shownLastNR.value ?? 3600 } : { enabled: false, value: 3600 });
    setIsPublic(config?.is_public ?? false);
  }, [config]);

  useEffect(() => {
    let cancelled = false;
    setFeedsLoading(true);
    deployApi
      .listFeedAlgos()
      .then((res) => {
        if (!cancelled) {
          const items = res?.feed_algos ?? [];
          setFeeds(items);
        }
      })
      .catch(() => {
        if (!cancelled) setFeeds([]);
      })
      .finally(() => {
        if (!cancelled) setFeedsLoading(false);
      });
    return () => { cancelled = true; };
  }, [deployApi, config]);

  const handleAddMainFeed = useCallback(() => {
    const idKey = (f) => f.algo_id;
    const firstAvailable = feeds.find((f) => !mainAlgos.some((c) => (c.algo_id) === idKey(f)));
    if (firstAvailable) {
      setMainAlgos((prev) => [...prev, { algo_id: idKey(firstAvailable), weight: 1 }]);
    } else {
      setMainAlgos((prev) => [...prev, { algo_id: idKey(feeds[0]) ?? 0, weight: 1 }]);
    }
  }, [feeds, mainAlgos]);

  const handleRemoveMainFeed = useCallback((index) => {
    setMainAlgos((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleMainFeedWeightChange = useCallback((index, weight) => {
    const v = Number(weight);
    if (Number.isNaN(v) || v < 0) return;
    setMainAlgos((prev) =>
      prev.map((c, i) => (i === index ? { ...c, weight: v } : c))
    );
  }, []);

  const handleMainFeedIdChange = useCallback((index, algoId) => {
    const id = algoId === '' ? null : Number(algoId);
    setMainAlgos((prev) =>
      prev.map((c, i) => (i === index ? { ...c, algo_id: id ?? (c.algo_id) } : c))
    );
  }, []);

  const handleSave = useCallback(async () => {
    if (!name?.trim()) {
      setSaveError('Name is required');
      return;
    }
    const validMainAlgos = mainAlgos.filter((c) => (c.algo_id) != null);
    if (validMainAlgos.length === 0) {
      setSaveError('At least one main feed algo is required');
      return;
    }
    if (primaryFallback) {
      const primFeed = feeds.find((f) => (f.algo_id) === Number(primaryFallback));
      if (primFeed && primFeed.feed_type !== 'minifeed') {
        setSaveError('Primary fallback must be a feed with feed_type = "minifeed"');
        return;
      }
    }
    if (secondaryFallback) {
      const secFeed = feeds.find((f) => (f.algo_id) === Number(secondaryFallback));
      if (secFeed && secFeed.feed_inputs !== 'none') {
        setSaveError('Secondary fallback must be a feed with feed_inputs = "none"');
        return;
      }
    }

    setSaveError(null);
    setSaveSuccess(false);
    setIsSaving(true);
    try {
      const feedConfig = {
        page_size: Number(pageSize) || PAGE_SIZE_DEFAULT,
        main_algos: validMainAlgos.map((c) => ({
          algo_id: c.algo_id,
          weight: c.weight ?? 1,
        })),
        main_max_cache_age_s: Number(mainMaxCacheAgeSec) || CACHE_AGE_DEFAULTS.main_max_cache_age_s,
        building_timeout_ms: Number(buildingTimeoutMs) || BUILDING_TIMEOUT_MS_DEFAULT,
        ...(primaryFallback ? {
          primary_fallback: Number(primaryFallback),
          primary_max_cache_age_s: Number(primaryMaxCacheAgeSec) || CACHE_AGE_DEFAULTS.primary_max_cache_age_s,
        } : {}),
        ...(secondaryFallback ? {
          secondary_fallback: Number(secondaryFallback),
          secondary_max_cache_age_s: Number(secondaryMaxCacheAgeSec) || CACHE_AGE_DEFAULTS.secondary_max_cache_age_s,
        } : {}),
        exclusion_rules: (() => {
          const rules = [];
          if (exclusionMaxItems.enabled && exclusionMaxItems.field?.trim()) {
            const v = Number(exclusionMaxItems.value);
            if (v >= 1) rules.push({ rule: 'max_items_with_same_field', field: exclusionMaxItems.field.trim(), value: v });
          }
          if (exclusionMinDistance.enabled && exclusionMinDistance.field?.trim()) {
            const d = Number(exclusionMinDistance.distance);
            if (d >= 1) rules.push({ rule: 'min_distance_between_items_with_same_field', field: exclusionMinDistance.field.trim(), distance: d });
          }
          if (exclusionHide1.enabled && exclusionHide1.field?.trim()) {
            rules.push({ rule: 'hide_items_with_same_field', field: exclusionHide1.field.trim() });
          }
          if (exclusionHide2.enabled && exclusionHide2.field?.trim()) {
            rules.push({ rule: 'hide_items_with_same_field', field: exclusionHide2.field.trim() });
          }
          if (exclusionItemsShownNTimes.enabled) {
            const v = Number(exclusionItemsShownNTimes.value);
            if (v >= 1) rules.push({ rule: 'exclude_items_shown_n_times', value: v });
          }
          if (exclusionItemsShownLastNSeconds.enabled) {
            const v = Number(exclusionItemsShownLastNSeconds.value);
            if (v > 0) rules.push({ rule: 'exclude_items_shown_last_n_seconds', value: v });
          }
          return rules;
        })(),
      };

      if (config?.config_id) {
        await deployApi.updateFeedConfig(config.config_id, {
          name: name.trim(),
          description: description.trim() || undefined,
          is_public: isPublic,
          feed_config: feedConfig,
        });
      } else {
        await deployApi.createFeedConfig({
          account_id: accountId,
          user_id: userId,
          name: name.trim(),
          description: description.trim() || undefined,
          is_public: isPublic,
          feed_config: feedConfig,
        });
      }
      setSaveSuccess(true);
      onSaved?.();
      if (!config?.config_id) onClear?.();
    } catch (err) {
      setSaveError(err?.message ?? 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  }, [
    name,
    description,
    pageSize,
    accountId,
    userId,
    mainAlgos,
    mainMaxCacheAgeSec,
    buildingTimeoutMs,
    primaryFallback,
    primaryMaxCacheAgeSec,
    secondaryFallback,
    secondaryMaxCacheAgeSec,
    exclusionMaxItems,
    exclusionMinDistance,
    exclusionHide1,
    exclusionHide2,
    exclusionItemsShownNTimes,
    exclusionItemsShownLastNSeconds,
    isPublic,
    config,
    feeds,
    deployApi,
    onSaved,
    onClear,
  ]);

  // ------- DSL <-> form helpers -------
  const algosById = useMemo(() => {
    const m = new Map();
    for (const a of feeds) m.set(Number(a.algo_id), a);
    return m;
  }, [feeds]);
  const algosByName = useMemo(() => {
    const m = new Map();
    for (const a of feeds) if (a?.name) m.set(a.name, a);
    return m;
  }, [feeds]);

  const currentFormState = useCallback(() => ({
    name,
    description,
    pageSize,
    mainAlgos: mainAlgos.map((c) => ({ algoId: c.algo_id, weight: c.weight })),
    mainMaxCacheAgeSec,
    buildingTimeoutMs,
    primaryFallback,
    primaryMaxCacheAgeSec,
    secondaryFallback,
    secondaryMaxCacheAgeSec,
    exclusionMaxItems,
    exclusionMinDistance,
    exclusionHide1,
    exclusionHide2,
    exclusionItemsShownNTimes,
    exclusionItemsShownLastNSeconds,
    isPublic,
  }), [
    name, description, pageSize, mainAlgos, mainMaxCacheAgeSec, buildingTimeoutMs,
    primaryFallback, primaryMaxCacheAgeSec, secondaryFallback, secondaryMaxCacheAgeSec,
    exclusionMaxItems, exclusionMinDistance, exclusionHide1, exclusionHide2,
    exclusionItemsShownNTimes, exclusionItemsShownLastNSeconds, isPublic,
  ]);

  const applyFormState = useCallback((form) => {
    if (form.name !== undefined) setName(String(form.name || ''));
    if (form.description !== undefined) setDescription(String(form.description || ''));
    if (form.pageSize !== undefined) setPageSize(form.pageSize);
    if (Array.isArray(form.mainAlgos)) {
      setMainAlgos(form.mainAlgos.map((c) => ({ algo_id: c.algo_id ?? c.algoId, weight: c.weight ?? 1 })));
    }
    if (form.mainMaxCacheAgeSec !== undefined) setMainMaxCacheAgeSec(form.mainMaxCacheAgeSec);
    if (form.buildingTimeoutMs !== undefined) setBuildingTimeoutMs(form.buildingTimeoutMs);
    if (form.primaryFallback !== undefined) setPrimaryFallback(form.primaryFallback);
    if (form.primaryMaxCacheAgeSec !== undefined) setPrimaryMaxCacheAgeSec(form.primaryMaxCacheAgeSec);
    if (form.secondaryFallback !== undefined) setSecondaryFallback(form.secondaryFallback);
    if (form.secondaryMaxCacheAgeSec !== undefined) setSecondaryMaxCacheAgeSec(form.secondaryMaxCacheAgeSec);
    if (form.exclusionMaxItems) setExclusionMaxItems(form.exclusionMaxItems);
    if (form.exclusionMinDistance) setExclusionMinDistance(form.exclusionMinDistance);
    if (form.exclusionHide1) setExclusionHide1(form.exclusionHide1);
    if (form.exclusionHide2) setExclusionHide2(form.exclusionHide2);
    if (form.exclusionItemsShownNTimes) setExclusionItemsShownNTimes(form.exclusionItemsShownNTimes);
    if (form.exclusionItemsShownLastNSeconds) setExclusionItemsShownLastNSeconds(form.exclusionItemsShownLastNSeconds);
    if (form.isPublic !== undefined) setIsPublic(Boolean(form.isPublic));
  }, []);

  const handleModeChange = useCallback((_e, next) => {
    if (next === mode) return;
    if (next === 'dsl') {
      setDslText(generateDsl(currentFormState(), { algosById }));
      setDslError(null);
      setMode('dsl');
    } else {
      const parsed = parseDsl(dslText, { algosByName });
      if (!parsed.ok) {
        setDslError(parsed.error);
        return;
      }
      applyFormState(parsed.value);
      setDslError(null);
      setMode('form');
    }
  }, [mode, dslText, currentFormState, applyFormState, algosById, algosByName]);

  // Mount CodeMirror when entering DSL mode
  useEffect(() => {
    if (mode !== 'dsl' || !editorContainerRef.current) return undefined;
    const initial = generateDsl(currentFormState(), { algosById });
    setDslText(initial);
    const state = EditorState.create({
      doc: initial,
      extensions: [
        javascript(),
        keymap.of(defaultKeymap),
        EditorView.lineWrapping,
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            setDslText(update.state.doc.toString());
            setDslError(null);
          }
        }),
      ],
    });
    const view = new EditorView({ state, parent: editorContainerRef.current });
    editorViewRef.current = view;
    return () => {
      view.destroy();
      editorViewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const algoId = (f) => f.algo_id;
  const getFeedLabel = (f) => `${f.name || `Feed algo #${algoId(f)}`} (inputs: ${f.feed_inputs ?? '—'})`;

  const getFeedsForPrimaryFallbackSelect = (currentId) => {
    const inList = feedsMinifeed.find((f) => algoId(f) === currentId);
    if (inList) return feedsMinifeed;
    if (currentId != null && currentId !== '') {
      const f = feeds.find((x) => algoId(x) === currentId);
      if (f) return [{ ...f, name: `${f.name || `Feed algo #${algoId(f)}`} (not a minifeed)` }, ...feedsMinifeed];
    }
    return feedsMinifeed;
  };

  const getFeedsForMainSelect = (currentId) => {
    const inList = feeds.find((f) => algoId(f) === currentId);
    if (inList) return feeds;
    if (currentId != null && currentId !== '') {
      return [{ algo_id: currentId, name: `Feed algo #${currentId}`, feed_inputs: '—' }, ...feeds];
    }
    return feeds;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Tabs
          value={mode}
          onChange={handleModeChange}
          variant="standard"
          sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
        >
          <Tab value="form" label="Form" />
          <Tab value="dsl" label="DSL" />
        </Tabs>
        {dslError && <Alert severity="error" onClose={() => setDslError(null)}>DSL parse error: {dslError}</Alert>}
        {mode === 'dsl' ? (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Edit the feed config as an algo-dsl call chain. Switching back to <strong>Form</strong> will
              parse this code into the form fields. In <code>.mainAlgos([...])</code> / <code>.primaryFallback()</code> /
              <code>.secondaryFallback()</code> you can use numeric ids or string algo names.
            </Typography>
            {feeds.length > 0 && (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1, p: 1, maxHeight: 140, overflowY: 'auto', fontSize: 12, bgcolor: 'grey.50' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Your algos (click to copy id):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {feeds.map((a) => (
                    <Chip
                      key={a.algo_id}
                      size="small"
                      label={`#${a.algo_id} · ${a.name} · ${a.feed_type || 'algorithm'}`}
                      onClick={() => { try { navigator.clipboard?.writeText(String(a.algo_id)); } catch {} }}
                      sx={{ fontFamily: 'monospace', fontSize: 11, cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            <Box
              ref={editorContainerRef}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, minHeight: 320, fontSize: 13, '& .cm-editor': { outline: 'none' }, '& .cm-scroller': { fontFamily: 'monospace' } }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                onClick={() => {
                  const parsed = parseDsl(dslText, { algosByName });
                  if (!parsed.ok) { setDslError(parsed.error); return; }
                  applyFormState(parsed.value);
                  setDslError(null);
                  setMode('form');
                  setTimeout(() => handleSave(), 0);
                }}
                disabled={isSaving}
              >
                {config?.config_id ? 'Update' : 'Create'} Feed Config
              </Button>
              {isSaving && <Typography variant="body2" color="text.secondary">Saving…</Typography>}
            </Box>
          </Box>
        ) : (
        <>
        <FormControlLabel
          control={<Checkbox checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />}
          label="Public"
        />
        {!config?.config_id && (
          <>
            <TextField
              size="small"
              label="Account ID"
              value={accountId ?? ''}
              placeholder="Account ID"
              fullWidth
              required
              disabled
            />
            <TextField
              size="small"
              label="User ID"
              value={userId ?? ''}
              placeholder="FMBD User ID"
              fullWidth
              disabled
            />
          </>
        )}
        <TextField
          size="small"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Feed Config name"
          fullWidth
          required
        />
        <TextField
          size="small"
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Feed Config description"
          fullWidth
          multiline
        />

        <TextField
          size="small"
          type="number"
          label="Page size"
          value={pageSize}
          onChange={(e) => {
            const v = e.target.value;
            setPageSize(v === '' ? '' : (Number(v) || PAGE_SIZE_DEFAULT));
          }}
          inputProps={{ min: 1 }}
          fullWidth
          helperText="Number of items per page (default: 25)"
        />

        <TextField
          size="small"
          type="number"
          label="Feed building timeout (ms)"
          value={buildingTimeoutMs}
          onChange={(e) => {
            const v = e.target.value;
            setBuildingTimeoutMs(v === '' ? '' : (Number(v) || BUILDING_TIMEOUT_MS_DEFAULT));
          }}
          fullWidth
        />

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Main feed algos <Box component="span" sx={{ color: 'error.main' }}>*</Box>
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddMainFeed}
              disabled={feedsLoading || feeds.length === 0}
            >
              Add feed algo
            </Button>
          </Box>
          {mainAlgos.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No main feed algos. Add feed algos from the list.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {mainAlgos.map((c, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    size="small"
                    select
                    label="Feed algo"
                    value={c.algo_id ?? ''}
                    onChange={(e) => handleMainFeedIdChange(idx, e.target.value)}
                    sx={{ flex: 1, minWidth: 120 }}
                  >
                    {getFeedsForMainSelect(c.algo_id).map((f) => (
                      <MenuItem key={algoId(f)} value={algoId(f)}>
                        {getFeedLabel(f)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small"
                    type="number"
                    label="Weight"
                    value={c.weight ?? 1}
                    onChange={(e) => handleMainFeedWeightChange(idx, e.target.value)}
                    sx={{ width: 100 }}
                  />
                  <IconButton size="small" onClick={() => handleRemoveMainFeed(idx)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <TextField
          size="small"
          type="number"
          label="Main feed max cache age (s)"
          value={mainMaxCacheAgeSec}
          onChange={(e) => setMainMaxCacheAgeSec(Number(e.target.value) || CACHE_AGE_DEFAULTS.main_max_cache_age_s)}
          fullWidth
        />

        <Divider sx={{ my: 1.5 }} />

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Primary Fallback
        </Typography>
        <TextField
          size="small"
          select
          label="Primary fallback"
          value={primaryFallback}
          onChange={(e) => setPrimaryFallback(e.target.value)}
          fullWidth
          helperText={feedsLoading ? 'Loading feed algos...' : 'Optional. In case the main feed algos are not available in cache, this fallback will be used. Only minifeeds are allowed here to optimize latency.'}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {getFeedsForPrimaryFallbackSelect(primaryFallback).map((f) => (
            <MenuItem key={algoId(f)} value={algoId(f)}>
              {getFeedLabel(f)}
            </MenuItem>
          ))}
        </TextField>

        {primaryFallback && (
          <TextField
            size="small"
            type="number"
            label="Primary fallback max cache age (s)"
            value={primaryMaxCacheAgeSec}
            onChange={(e) => setPrimaryMaxCacheAgeSec(Number(e.target.value) || CACHE_AGE_DEFAULTS.primary_max_cache_age_s)}
            fullWidth
          />
        )}

        <Divider sx={{ my: 1.5 }} />

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Secondary Fallback
        </Typography>
        <TextField
          size="small"
          select
          label="Secondary fallback"
          value={secondaryFallback}
          onChange={(e) => setSecondaryFallback(e.target.value)}
          fullWidth
          helperText={feedsLoading ? 'Loading feed algos...' : "Optional. Fast fallback when main feed algos are not available in cache and the primary fallback times out. Only non-personalized feed algos (feed_inputs=none) are allowed."}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {feedsWithNoneInputs.map((f) => (
            <MenuItem key={algoId(f)} value={algoId(f)}>
              {getFeedLabel(f)}
            </MenuItem>
          ))}
        </TextField>

        {secondaryFallback && (
          <TextField
            size="small"
            type="number"
            label="Secondary fallback max cache age (s)"
            value={secondaryMaxCacheAgeSec}
            onChange={(e) => setSecondaryMaxCacheAgeSec(Number(e.target.value) || CACHE_AGE_DEFAULTS.secondary_max_cache_age_s)}
            fullWidth
          />
        )}

        <Divider sx={{ my: 1.5 }} />

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Exclusion rules
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={exclusionMaxItems.enabled}
                  onChange={(e) => setExclusionMaxItems((p) => ({ ...p, enabled: e.target.checked }))}
                />
              }
              label="Max items with same field"
            />
            {exclusionMaxItems.enabled && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1, ml: 3 }}>
                <TextField
                  size="small"
                  label="Field"
                  value={exclusionMaxItems.field}
                  onChange={(e) => setExclusionMaxItems((p) => ({ ...p, field: e.target.value }))}
                  placeholder="e.g. author_fid"
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Max value"
                  value={exclusionMaxItems.value}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setExclusionMaxItems((p) => ({ ...p, value: (v >= 1 ? v : 1) }));
                  }}
                  inputProps={{ min: 1 }}
                  sx={{ width: 100 }}
                />
              </Box>
            )}
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={exclusionMinDistance.enabled}
                  onChange={(e) => setExclusionMinDistance((p) => ({ ...p, enabled: e.target.checked }))}
                />
              }
              label="Min distance between items with same field"
            />
            {exclusionMinDistance.enabled && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1, ml: 3 }}>
                <TextField
                  size="small"
                  label="Field"
                  value={exclusionMinDistance.field}
                  onChange={(e) => setExclusionMinDistance((p) => ({ ...p, field: e.target.value }))}
                  placeholder="e.g. author_fid"
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Distance"
                  value={exclusionMinDistance.distance}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setExclusionMinDistance((p) => ({ ...p, distance: (v >= 1 ? v : 1) }));
                  }}
                  inputProps={{ min: 1 }}
                  sx={{ width: 100 }}
                />
              </Box>
            )}
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={exclusionHide1.enabled}
                  onChange={(e) => setExclusionHide1((p) => ({ ...p, enabled: e.target.checked }))}
                />
              }
              label="Hide items with same field"
            />
            {exclusionHide1.enabled && (
              <Box sx={{ mt: 1, ml: 3 }}>
                <TextField
                  size="small"
                  label="Field"
                  value={exclusionHide1.field}
                  onChange={(e) => setExclusionHide1((p) => ({ ...p, field: e.target.value }))}
                  placeholder="e.g. author_fid"
                  fullWidth
                />
              </Box>
            )}
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={exclusionHide2.enabled}
                  onChange={(e) => setExclusionHide2((p) => ({ ...p, enabled: e.target.checked }))}
                />
              }
              label="Also hide items with same field"
            />
            {exclusionHide2.enabled && (
              <Box sx={{ mt: 1, ml: 3 }}>
                <TextField
                  size="small"
                  label="Field"
                  value={exclusionHide2.field}
                  onChange={(e) => setExclusionHide2((p) => ({ ...p, field: e.target.value }))}
                  placeholder="e.g. author_fid"
                  fullWidth
                />
              </Box>
            )}
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={exclusionItemsShownNTimes.enabled}
                  onChange={(e) => setExclusionItemsShownNTimes((p) => ({ ...p, enabled: e.target.checked }))}
                />
              }
              label="Exclude items shown N times"
            />
            {exclusionItemsShownNTimes.enabled && (
              <Box sx={{ mt: 1, ml: 3 }}>
                <TextField
                  size="small"
                  type="number"
                  label="N (≥1)"
                  value={exclusionItemsShownNTimes.value}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setExclusionItemsShownNTimes((p) => ({ ...p, value: (v >= 1 ? v : 1) }));
                  }}
                  inputProps={{ min: 1 }}
                  sx={{ width: 120 }}
                />
              </Box>
            )}
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={exclusionItemsShownLastNSeconds.enabled}
                  onChange={(e) => setExclusionItemsShownLastNSeconds((p) => ({ ...p, enabled: e.target.checked }))}
                />
              }
              label="Exclude items shown in last N seconds"
            />
            {exclusionItemsShownLastNSeconds.enabled && (
              <Box sx={{ mt: 1, ml: 3 }}>
                <TextField
                  size="small"
                  type="number"
                  label="N seconds (>0)"
                  value={exclusionItemsShownLastNSeconds.value}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setExclusionItemsShownLastNSeconds((p) => ({ ...p, value: (v > 0 ? v : 1) }));
                  }}
                  inputProps={{ min: 1 }}
                  sx={{ width: 120 }}
                />
              </Box>
            )}
          </Box>
        </Box>

        {(saveError || saveSuccess) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {saveSuccess && <Alert severity="success">Feed Config saved successfully</Alert>}
            {saveError && <Alert severity="error">{saveError}</Alert>}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={isSaving}
          >
            {config?.config_id ? 'Update' : 'Create'} Feed Config
          </Button>
          {isSaving && (
            <Typography variant="body2" color="text.secondary">
              Saving…
            </Typography>
          )}
        </Box>
        </>
        )}
      </Box>
    </Box>
  );
}

export default ConfigForm;
